/// <amd-module name="CodeCoverage/CodeCoverageCiFileCoveragePane/CodeCoverageCiFileCoverageView" />
import * as React from "react";

import { ICancelReason } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { IEditorProps } from "VSS/Features/Monaco/Contracts";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Card } from "VSSUI/Card";
import { CustomHeader, HeaderBackButton, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";

import { GutterAdornment } from "CodeCoverage/CodeCoverageCiFileCoveragePane/CodeEditorGutterAdornments/AdornmentContracts";
import { CodeCoverageAdornmentProvider } from "CodeCoverage/CodeCoverageCiFileCoveragePane/CodeEditorGutterAdornments/CodeCoverageAdornmentProvider";
import { MonacoAdornmentControlManager } from "CodeCoverage/CodeCoverageCiFileCoveragePane/CodeEditorGutterAdornments/CodeEditor.GutterAdornments";
import * as Resources from "CodeCoverage/CodeCoverageCiFileCoveragePane/Resources";
import { ISourceProviderFactoryService } from "CodeCoverage/CodeCoverageSourceProvider/SourceProviderFactoryService";
import { PerfScenarios } from "CodeCoverage/ViewCommon/Constants";
import { IContentInfo } from "CodeCoverage/ViewCommon/Contracts/Common";
import { ISourceProvider } from "CodeCoverage/ViewCommon/Contracts/ISourceProvider";
import { RunViewArtifact } from "CodeCoverage/ViewCommon/RunViewArtifact";
import { getCoverageChangeIconName } from "CodeCoverage/ViewCommon/Helpers";

export interface ICodeCoverageCiFileCoverageProps {
    runViewArtifact: RunViewArtifact;
    filePath: string;
    coveragePercentage: number;
    coverageChangePercentage: number;
    totalLines: number;
    notCoveredLines: number;
    coveredLines: number;
    partiallyCoveredLines: number;
    hideCoverageChangePercentage?: boolean;
    onMount?: () => void;
    onClose?: () => void;
}

export interface ICodeCoverageCiFileCoverageState {
    isEditorLoaded: boolean;
    monacoTheme: monaco.editor.BuiltinTheme;
    content: string;
}

export class CodeCoverageCiFileCoverageView extends VssComponent<ICodeCoverageCiFileCoverageProps, ICodeCoverageCiFileCoverageState> {
    private editor?: monaco.editor.IEditor;
    private themeService: IVssThemeService;

    constructor(props: ICodeCoverageCiFileCoverageProps, context: IVssComponentContext) {
        super(props, context);

        this.themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
        this.state = {
            isEditorLoaded: false,
            monacoTheme: this._getEditorTheme(),
            content: Resources.EditorContentLoadingMessage
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.props.onMount) {
            this.props.onMount();
        }

        this.addEventListener(window, "resize", this._onResizeHandler);
        this.themeService.subscribe(this._onThemeChanged, "themeChanged");
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this.themeService.unsubscribe(this._onThemeChanged, "themeChanged");
    }

    private fetchSourceContentAndAdornments(sourceInfoProvider: ISourceProvider, filePath: string): void {
        let sourceCodeData: string;

        const info: IContentInfo = {
            id: this.props.runViewArtifact.buildId.toString(),
            projectId: this.props.runViewArtifact.projectId,
            repositoryId: this.props.runViewArtifact.sourceRepositoryId,
            version: this.props.runViewArtifact.sourceVersion,
            path: filePath
        };
        const contentPromise = this.trackPromise(sourceInfoProvider.fetchContent(info)).promise.then((result: string) => {
            sourceCodeData = result;
            this.setState({
                content: sourceCodeData
            });
        });
        const adornmentProvider = new CodeCoverageAdornmentProvider(this.context.pageContext);
        const adornmentsPromise = adornmentProvider.getGutterAdornments(
            { path: filePath, scope: "all" },
            this.props.runViewArtifact.projectId,
            this.props.runViewArtifact.buildId
        );
        this.trackPromise(Promise.all([contentPromise, adornmentsPromise])).promise.then(
            ([contentResult, adornmentsResult]) => {
                this.setAdornments(adornmentsResult);
                const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                performanceService.endScenario(PerfScenarios.Area, PerfScenarios.FileCoverageDetails, true);
            },
            (error: ICancelReason) => {
                if (!error.isCanceled) {
                    sourceCodeData = Resources.FetchErrorMessage;
                    this.setState({
                        content: sourceCodeData
                    });
                    $DEBUG && console.error(Resources.FetchErrorMessage);
                }
            }
        );
    }

    public render(): JSX.Element {
        const { coverageChangePercentage } = this.props;

        const legacyComponentLoading: () => JSX.Element = () => (
            <Spinner size={SpinnerSize.large} className="flex-grow" label={Resources.LoadingEditorMessage} />
        );

        const options: monaco.editor.IEditorConstructionOptions = {
            readOnly: true,
            minimap: {
                enabled: false
            },
            scrollbar: {
                useShadows: false
            },
            lineNumbersMinChars: 8,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            overviewRulerBorder: false,
            folding: false
        };

        const buttonProps = {
            iconName: "Back",
            onClick: () => {
                if (this.props.onClose) {
                    this.props.onClose();
                }
            }
        };

        return (
            <div className="file-coverage-details-pane">
                <div className="details-pane">
                    <CustomHeader className="bolt-header-with-commandbar">
                        <HeaderBackButton buttonProps={buttonProps} />
                        <HeaderTitleArea>
                            <HeaderTitleRow>
                                <HeaderTitle titleSize={TitleSize.Large}>
                                    <Tooltip text={this.props.filePath} overflowOnly={true}>
                                        <div className="text-ellipsis">{this.props.filePath}</div>
                                    </Tooltip>
                                </HeaderTitle>
                            </HeaderTitleRow>
                            <HeaderDescription>
                                <div className="file-coverage-details-box">
                                    <span className="coverage-percentage">
                                        {format(Resources.CoveragePercentageFormat, this.props.coveragePercentage.toFixed(2))}
                                    </span>
                                    {!this.props.hideCoverageChangePercentage && (
                                        <>
                                            <Icon iconName={getCoverageChangeIconName(coverageChangePercentage)} className="indicator-icon" />
                                            <span className="coverage-changed">
                                                {format(Resources.CoveragePercentageFormat, Math.abs(coverageChangePercentage))}
                                            </span>
                                        </>
                                    )}
                                    <span className="coverage-details">
                                        {format(
                                            Resources.SummaryDetailsTextFormat,
                                            this.props.coveredLines,
                                            this.props.partiallyCoveredLines,
                                            this.props.notCoveredLines
                                        )}
                                    </span>
                                </div>
                            </HeaderDescription>
                        </HeaderTitleArea>
                    </CustomHeader>
                </div>

                <Card className="editor-container">
                    <WrappedComponent<IEditorProps>
                        dependencies={["ms.vss-features.editor"]}
                        wrappedType="code-editor"
                        wrappedProps={{
                            className: "editor-pane",
                            editorOptions: options,
                            monacoTheme: this.state.monacoTheme,
                            content: {
                                content: this.state.content,
                                contentUri: this.props.filePath
                            },
                            editorRef: this._onEditorCreated
                        }}
                        loadingComponent={legacyComponentLoading}
                    />
                </Card>
            </div>
        );
    }

    private setAdornments(adornments: GutterAdornment[]): void {
        if (this.editor && adornments) {
            const adornmentControlManager = new MonacoAdornmentControlManager(this.editor);
            adornmentControlManager.setAdornments(adornments);
        }
    }

    private _onEditorCreated = (editor: monaco.editor.IEditor) => {
        if (editor) {
            this.editor = editor;
            this.setState({
                content: Resources.EditorContentLoadingMessage
            });
        }
        const sourceProviderService = this.context.pageContext.getService<ISourceProviderFactoryService>("ISourceProviderFactoryService");
        const sourceInfoProvider = sourceProviderService.getSourceInfoProvider(
            this.context.pageContext,
            this.props.runViewArtifact.sourceRepositoryType
        );
        if (sourceInfoProvider) {
            this.fetchSourceContentAndAdornments(sourceInfoProvider, this.props.filePath || "");
        }
    };

    private _onResizeHandler = () => {
        if (this.editor) {
            this.editor.layout();
        }
    };

    private _onThemeChanged = () => {
        const monacoTheme = this._getEditorTheme();
        this.setState({ monacoTheme });
    };

    private _getEditorTheme(): monaco.editor.BuiltinTheme {
        const currentTheme = this.themeService.getCurrentTheme();
        return currentTheme && currentTheme.isDark ? "vs-dark" : "vs";
    }
}

Components.add("CC.CI.FileCoverageViewer", CodeCoverageCiFileCoverageView);
